import type { SecretMetadata } from '@/types'
import { formatKstDateTime } from '@/utils/datetime'

interface Props {
  secrets: SecretMetadata[]
  onReplace: (secret: SecretMetadata) => void
  onDelete: (secret: SecretMetadata) => void
  loading?: boolean
}

function formatDate(ts: string | null): string {
  return formatKstDateTime(ts, '—')
}

export default function SecretTable({ secrets, onReplace, onDelete, loading }: Props) {
  if (loading) {
    return <p style={{ color: '#888', padding: '20px 0' }}>Loading secrets...</p>
  }
  if (secrets.length === 0) {
    return (
      <p style={{ color: '#888', textAlign: 'center', padding: '32px 0' }}>
        No secrets found.
      </p>
    )
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            {['Name', 'Scope', 'Scope key', 'Value', 'Ver.', 'Created by', 'Updated at', 'Actions'].map((h) => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {secrets.map((s, i) => (
            <tr key={s.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ ...tdStyle, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>
                {s.name}
              </td>
              <td style={tdStyle}>
                <ScopeBadge scope={s.scope} />
              </td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12, color: '#555' }}>
                {s.scope_key}
              </td>
              <td style={{ ...tdStyle, color: '#888', fontFamily: 'monospace', fontSize: 12 }}>
                [encrypted]
              </td>
              <td style={{ ...tdStyle, textAlign: 'center', color: '#555', fontSize: 13 }}>
                v{s.version}
              </td>
              <td style={{ ...tdStyle, color: '#888', fontSize: 12 }}>
                {s.created_by ?? '—'}
              </td>
              <td style={{ ...tdStyle, color: '#888', fontSize: 12, whiteSpace: 'nowrap' }}>
                {formatDate(s.updated_at)}
              </td>
              <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                <button
                  onClick={() => onReplace(s)}
                  style={actionBtnStyle}
                  title="Replace secret value"
                >
                  Replace
                </button>
                <button
                  onClick={() => onDelete(s)}
                  style={{ ...actionBtnStyle, color: '#c62828', marginLeft: 6 }}
                  title="Delete secret"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ScopeBadge({ scope }: { scope: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    global: { bg: '#e3f2fd', text: '#1565c0' },
    site:   { bg: '#e8f5e9', text: '#2e7d32' },
    node:   { bg: '#fff8e1', text: '#f57f17' },
  }
  const c = colors[scope] ?? { bg: '#f0f0f0', text: '#555' }
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: c.bg,
      color: c.text,
    }}>
      {scope}
    </span>
  )
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  overflow: 'hidden',
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 700,
  color: '#555',
  background: '#f0f0f4',
  borderBottom: '1px solid #e0e0e0',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
}

const actionBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 12,
  background: '#f0f0f4',
  border: '1px solid #d0d0d0',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: '#3949ab',
}
